/**
 * Line upgrades and paint, and what a station has in stock.
 *
 * ── Linear, and gated by supply ─────────────────────────────────────────────
 *
 * Every line has the same hundred points of health. What an upgrade buys is
 * *strength*, which reduces incoming damage — so the progression is one axis
 * with no trade-off to agonise over, and a tougher hull is simply a hull whose
 * owner has been further afield.
 *
 * Stock limits how much you can fit in one go, not whether you can get it at
 * all. Upgrades are paid for in linelits; the shelf is there so that a single
 * station cannot re-plate an entire hull in one sitting, which keeps a reason
 * to travel without making the good grades a lottery. Anything scarcer than
 * that stops respecting the player's time — hunting twenty stations for one
 * component is not exploration, it is a chore.
 *
 * Paint is stocked as a *quantity*, not a palette. A station either has paint
 * or does not, and while it has some you may use any colour — a station that
 * happens to lack the one colour you wanted is a pointless disappointment,
 * where a station that has run low is a reason to move on.
 */
import { shelfEra } from '../world/clock.js';
import { Rng } from '../world/rng.js';
/** Health is the same for every line, upgraded or not. */
export const LINE_HEALTH = 100;
export const LINE_GRADES = [
    { id: 'standard', name: 'Standard', strength: 1, color: '#8fb8de', availability: 1 },
    { id: 'reinforced', name: 'Reinforced', strength: 3, color: '#7fb46f', availability: 0.95 },
    { id: 'plated', name: 'Plated', strength: 6, color: '#ffd479', availability: 0.85 },
    { id: 'composite', name: 'Composite', strength: 10, color: '#c98bff', availability: 0.6 },
    { id: 'sintered', name: 'Sintered', strength: 15, color: '#ff8f6b', availability: 0.35 },
];
export function lineGrade(id) {
    return LINE_GRADES.find((grade) => grade.id === id);
}
/** The grade a line is currently in, matched on the stat that defines it. */
export function gradeOfStrength(strength) {
    return ([...LINE_GRADES].reverse().find((grade) => strength >= grade.strength) ?? LINE_GRADES[0]);
}
/**
 * What an unpainted line looks like.
 *
 * White, because paint is now its own channel on every wall — see the editor —
 * and a wall nobody has painted should read as *unpainted* rather than as
 * quietly wearing one of the eight colours you can buy. The old default was the
 * same sky blue as Hull Grey, so a fresh hull looked like a deliberate choice
 * and repainting it to that colour did nothing visible.
 */
export const DEFAULT_LINE_COLOR = '#ffffff';
export const PAINTS = [
    { id: 'hull-grey', name: 'Hull Grey', color: '#8fb8de' },
    { id: 'oxide', name: 'Oxide', color: '#c4693f' },
    { id: 'signal', name: 'Signal', color: '#ffd479' },
    { id: 'moss', name: 'Moss', color: '#5f9e6a' },
    { id: 'void', name: 'Void', color: '#2b3550' },
    { id: 'ember', name: 'Ember', color: '#ff6b5f' },
    { id: 'orchid', name: 'Orchid', color: '#c98bff' },
    { id: 'pearl', name: 'Pearl', color: '#e8f0ff' },
];
/**
 * What a station has on the shelf.
 *
 * Derived from the seed and the station's own id, like everything else, so it
 * is the same for every player without being stored anywhere — and a station
 * known for carrying good plating stays known for it.
 *
 * Standard grade is always unlimited: it is what a line is made of, not an
 * upgrade, and running out of it would mean being unable to build at all.
 */
export function stationStock(seed, stationId, era = shelfEra()) {
    // The period is part of the seed, so a shelf is genuinely a different shelf
    // two hours later rather than the same one with a counter reset. Same window
    // as the trader's, because "come back later" should mean one thing per station.
    const rng = Rng.forEntity(seed, 'station-stock', stationId, String(era));
    const grades = {};
    for (const grade of LINE_GRADES) {
        if (grade.availability >= 1) {
            grades[grade.id] = Infinity;
            continue;
        }
        // Most stations carry most grades. What varies is how many, so the limit
        // is on re-plating a whole hull at once rather than on finding the grade
        // in the first place — you pay for these in linelits, and a scavenger hunt
        // on top of the price is one gate too many.
        grades[grade.id] = rng.bool(grade.availability)
            ? rng.int(4, Math.max(6, Math.round(30 * grade.availability)))
            : 0;
    }
    return { grades, paint: rng.int(6, 30) };
}
/**
 * What a fitter charges to put a grade of plating on a line, in linelits.
 *
 * By the metre, because that is what is being covered — the same job on a span
 * twice as long is twice the work and twice the material. And by tier on top of
 * that, so the strong stuff is a decision rather than a formality: sintered is
 * four times the rate of reinforced, and fifteen times the strength.
 *
 * Standard is free. It is what a line is made of rather than an upgrade, and
 * charging to put a hull back to ordinary would make an unlucky trip cost twice.
 */
export const PLATING_RATE = 5;
/** Multiplier on the rate, by grade. Standard is not plating. */
const PLATING_TIER = {
    standard: 0,
    reinforced: 1,
    plated: 2,
    composite: 3,
    sintered: 4,
};
export function platingCost(gradeId, length) {
    return PLATING_RATE * length * (PLATING_TIER[gradeId] ?? 0);
}
/**
 * What a coat of paint costs, per line.
 *
 * Flat, and cheap. Paint changes nothing about how a ship flies, so pricing it
 * by the metre would tax long hulls for having a bigger canvas — which is a
 * penalty on the one thing in the editor that is purely yours.
 */
export const PAINT_COST = 5;
//# sourceMappingURL=upgrades.js.map